import type { RestClient } from './types';
import { CommandError } from './types';

export interface RestOptions {
  base: string;
  headers?: () => Record<string, string>;
  fetchImpl?: typeof fetch;
}

/** Thin JSON fetch wrapper. Cookies carry the session (cloud); the local shell adds a bearer after PIN unlock. */
export function createRest(opts: RestOptions): RestClient {
  const f = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const url = (path: string) => (path.startsWith('http') ? path : `${opts.base}${path}`);
  async function call<T>(method: string, path: string, body?: unknown, init?: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await f(url(path), {
        method,
        credentials: 'same-origin',
        ...init,
        headers: {
          Accept: 'application/json',
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          'X-Requested-With': 'glyph',
          ...(opts.headers?.() ?? {}),
          ...(init?.headers as Record<string, string> | undefined),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (e) {
      throw new CommandError((e as Error).message, 'network');
    }
    if (!res.ok) {
      let detail: unknown;
      try {
        detail = await res.json();
      } catch {
        detail = await res.text().catch(() => '');
      }
      const code = res.status === 403 || res.status === 401 ? 'forbidden' : res.status === 409 ? 'offline' : res.status === 504 ? 'timeout' : 'rejected';
      const msg = (detail as { detail?: string } | undefined)?.detail ?? `${res.status} ${res.statusText}`;
      throw new CommandError(String(msg), code, res.status, detail);
    }
    if (res.status === 204) return undefined as T;
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('text/html')) {
      // A SPA fallback page: the route does not exist on this transport (e.g. a cloud-only
      // resource asked of the device agent). Fail the query instead of handing HTML to a screen.
      throw new Error(`no such route on this transport: ${path}`);
    }
    return (ct.includes('json') ? await res.json() : await res.text()) as T;
  }
  return {
    get: (p, i) => call('GET', p, undefined, i),
    post: (p, b, i) => call('POST', p, b ?? {}, i),
    patch: (p, b, i) => call('PATCH', p, b ?? {}, i),
    put: (p, b, i) => call('PUT', p, b ?? {}, i),
    del: (p, i) => call('DELETE', p, undefined, i),
    url,
  };
}
